import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Users, Search, UserMinus } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { FollowButton } from '../components/profile/FollowButton';
import { User } from '../types';
import { useAuthStore } from '../store/authStore';
import { getFollowers, getFollowing, removeFollower } from '../lib/followService';
import { supabase } from '../lib/supabase';

export const FollowersPage: React.FC = () => {
  const { username, type } = useParams<{ username: string; type: 'followers' | 'following' }>();
  const { user: currentUser } = useAuthStore();
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  const isCurrentUser = profileUser && currentUser && (
    profileUser.username === currentUser.email?.split('@')[0] ||
    profileUser.id === currentUser.id
  );
  const pageType = type || 'followers';

  useEffect(() => {
    if (username) {
      loadProfileAndUsers();
    }
  }, [username, type]);

  const loadProfileAndUsers = async () => {
    setLoading(true);
    try {
      // Find user by username
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('username', username)
        .single();

      if (error) throw error;

      const user: User = {
        id: profiles.id,
        username: profiles.username,
        displayName: profiles.full_name || profiles.username,
        profileImage: profiles.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
        bio: profiles.bio || '',
        followerCount: 0,
        followingCount: 0,
        postCount: 0,
      };

      setProfileUser(user);

      // Load followers or following
      const data = pageType === 'followers' 
        ? await getFollowers(user.id)
        : await getFollowing(user.id);
      setUsers(data);
    } catch (error) {
      console.error(`Error loading ${pageType}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFollower = async (followerId: string) => {
    if (!isCurrentUser || !profileUser) return;

    setRemovingIds(prev => new Set(prev).add(followerId));
    try {
      await removeFollower(profileUser.id, followerId);
      setUsers(prev => prev.filter(user => user.id !== followerId));
    } catch (error) {
      console.error('Error removing follower:', error);
      alert('Failed to remove follower. Please try again.');
    } finally {
      setRemovingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(followerId);
        return newSet;
      });
    }
  };

  const handleFollowChange = (userId: string, isFollowing: boolean) => {
    if (pageType === 'following' && !isFollowing) {
      // If we unfollowed someone, remove them from the following list
      setUsers(prev => prev.filter(user => user.id !== userId));
    }
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 max-w-screen-md">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 max-w-screen-md">
          <div className="text-center py-12">
            <p className="text-gray-600">User not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 max-w-screen-md">
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center">
              <Link to={`/profile/${username}`} className="text-gray-600 mr-4">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-lg font-medium">{username}</h1>
                <p className="text-sm text-gray-500">
                  {users.length} {pageType}
                </p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${pageType}...`}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Users List */}
          <div>
            {filteredUsers.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                    <Link to={`/profile/${user.username}`} className="flex items-center flex-1">
                      <img
                        src={user.profileImage}
                        alt={user.username}
                        className="h-12 w-12 rounded-full object-cover mr-4"
                      />
                      <div>
                        <p className="font-medium text-gray-900">{user.username}</p>
                        <p className="text-sm text-gray-500">{user.displayName}</p>
                        {user.bio && (
                          <p className="text-xs text-gray-400 mt-1 max-w-xs truncate">
                            {user.bio}
                          </p>
                        )}
                      </div>
                    </Link>
                    
                    <div className="flex items-center space-x-2">
                      {user.id !== currentUser?.id && (
                        <FollowButton
                          userId={user.id}
                          username={user.username}
                          onFollowChange={(isFollowing) => handleFollowChange(user.id, isFollowing)}
                          size="sm"
                        />
                      )}
                      
                      {isCurrentUser && pageType === 'followers' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveFollower(user.id)}
                          disabled={removingIds.has(user.id)}
                          isLoading={removingIds.has(user.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">
                  {searchQuery 
                    ? 'No users found' 
                    : `No ${pageType} yet`
                  }
                </p>
                <p className="text-sm">
                  {searchQuery 
                    ? 'Try searching for a different name'
                    : pageType === 'followers' 
                      ? `When people follow ${isCurrentUser ? 'you' : username}, they'll appear here.`
                      : `When ${isCurrentUser ? 'you' : username} follows people, they'll appear here.`
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};